import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart, ArrowLeft, ArrowRight, Package } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
export function Cart() {
  const {
    cart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal
  } = useAppContext();
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'hosting':
        return 'Web Hosting';
      case 'radio':
        return 'Radio Station';
      case 'reseller':
        return 'Reseller';
      case 'shoutcast':
        return 'Shoutcast';
      case 'shoutcast-reseller':
        return 'Shoutcast Reseller';
      default:
        return category;
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'hosting':
        return 'bg-fox-500/10 text-fox-500';
      case 'radio':
        return 'bg-amber-500/10 text-amber-500';
      case 'reseller':
        return 'bg-fox-500/10 text-fox-500';
      case 'shoutcast':
        return 'bg-purple-500/10 text-purple-500';
      case 'shoutcast-reseller':
        return 'bg-cyan-500/10 text-cyan-500';
      default:
        return 'bg-slate-800 text-slate-400';
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950" data-id="element-2137">
      <Navbar data-id="element-2138" />
      <main className="pt-32 pb-24" data-id="element-2139">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-id="element-2140">
          <div className="flex items-center justify-between mb-10" data-id="element-2141">
            <div data-id="element-2142">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2 flex items-center gap-3" data-id="element-2143">
                <ShoppingCart className="text-fox-500" size={32} data-id="element-2144" />
                Shopping Cart
              </h1>
              <p className="text-slate-400" data-id="element-2145">
                {cart.length === 0 ? 'Your cart is empty.' : `${cart.reduce((s, i) => s + i.quantity, 0)} item${cart.reduce((s, i) => s + i.quantity, 0) !== 1 ? 's' : ''} in your cart`}
              </p>
            </div>
            {cart.length > 0 && <button onClick={clearCart} className="text-sm text-slate-400 hover:text-red-400 transition-colors" data-id="element-2146">
                Clear Cart
              </button>}
          </div>

          {cart.length === 0 ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl" data-id="element-2147">
              <Package size={64} className="text-slate-700 mx-auto mb-6" data-id="element-2148" />
              <h2 className="text-2xl font-heading font-bold text-white mb-3" data-id="element-2149">
                Nothing here yet
              </h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto" data-id="element-2150">
                Browse our pricing plans and add services to your cart to get
                started.
              </p>
              <Link to="/#pricing" className="inline-flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-3 rounded-xl font-medium transition-colors" data-id="element-2151">
                <ArrowLeft size={18} data-id="element-2152" />
                Browse Plans
              </Link>
            </motion.div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2153">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-id="element-2154">
                {cart.map((item, i) => <motion.div key={`${item.packageId}-${item.billing}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.05
            }} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2155">
                    <div className="flex-1" data-id="element-2156">
                      <div className="flex items-center gap-2 mb-2" data-id="element-2157">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCategoryColor(item.category)}`} data-id="element-2158">
                          {getCategoryLabel(item.category)}
                        </span>
                        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full" data-id="element-2159">
                          {item.billing}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white" data-id="element-2160">
                        {item.name}
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-2161">
                        ${item.price.toFixed(2)}/
                        {item.billing === 'monthly' ? 'mo' : 'yr'}
                      </p>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-2162">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg" data-id="element-2163">
                        <button onClick={() => updateCartQuantity(item.packageId, item.quantity - 1)} className="p-2 text-slate-400 hover:text-white transition-colors" data-id="element-2164">
                          <Minus size={14} data-id="element-2165" />
                        </button>
                        <span className="text-white font-medium w-6 text-center" data-id="element-2166">
                          {item.quantity}
                        </span>
                        <button onClick={() => updateCartQuantity(item.packageId, item.quantity + 1)} className="p-2 text-slate-400 hover:text-white transition-colors" data-id="element-2167">
                          <Plus size={14} data-id="element-2168" />
                        </button>
                      </div>

                      {/* Line Total */}
                      <span className="text-white font-bold w-20 text-right" data-id="element-2169">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>

                      {/* Remove */}
                      <button onClick={() => removeFromCart(item.packageId)} className="p-2 text-slate-500 hover:text-red-500 transition-colors" data-id="element-2170">
                        <Trash2 size={18} data-id="element-2171" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>

              {/* Order Summary */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="lg:col-span-1" data-id="element-2172">
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sticky top-28" data-id="element-2173">
                  <h2 className="text-xl font-heading font-bold text-white mb-6" data-id="element-2174">
                    Order Summary
                  </h2>

                  <div className="space-y-4 mb-6" data-id="element-2175">
                    {cart.map(item => <div key={`${item.packageId}-${item.billing}`} className="flex justify-between text-sm" data-id="element-2176">
                        <span className="text-slate-400 truncate mr-2" data-id="element-2177">
                          {item.name} x{item.quantity}
                        </span>
                        <span className="text-white font-medium shrink-0" data-id="element-2178">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>)}
                  </div>

                  <div className="border-t border-slate-800 pt-4 mb-6" data-id="element-2179">
                    <div className="flex justify-between mb-2" data-id="element-2180">
                      <span className="text-slate-400" data-id="element-2181">Subtotal</span>
                      <span className="text-white font-medium" data-id="element-2182">
                        ${cartTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-2183">
                      <span className="text-slate-400" data-id="element-2184">Tax</span>
                      <span className="text-slate-500 text-sm" data-id="element-2185">
                        Calculated at checkout
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-slate-800 pt-4 mb-6" data-id="element-2186">
                    <div className="flex justify-between" data-id="element-2187">
                      <span className="text-white font-bold text-lg" data-id="element-2188">
                        Total
                      </span>
                      <span className="text-white font-bold text-lg" data-id="element-2189">
                        ${cartTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Link to="/checkout" className="w-full bg-fox-600 hover:bg-fox-500 text-white font-medium py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(234,88,12,0.2)] hover:shadow-[0_0_25px_rgba(234,88,12,0.4)]" data-id="element-2190">
                    Proceed to Checkout <ArrowRight size={18} data-id="element-2191" />
                  </Link>

                  <Link to="/#pricing" className="w-full mt-3 py-3 rounded-xl font-medium text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-2192">
                    <ArrowLeft size={16} data-id="element-2193" />
                    Continue Shopping
                  </Link>
                </div>
              </motion.div>
            </div>}
        </div>
      </main>
      <Footer data-id="element-2194" />
    </motion.div>;
}